/**
 * `GET /goals/{id}?enrich_trackers=true` → 弹窗 `DiagnosisUnlockModalPayload`。
 */

import {
  GoalStatusEnum,
  type Goal,
  type Tracker,
} from "@/src/features/api/TrackerApi";
import { parseDiagnosisContentFields } from "./parseDiagnosisContentFields";
import type { DiagnosisUnlockModalPayload } from "./types";

function mapTrackersToSpecs(
  trackers: Tracker[] | undefined,
): DiagnosisUnlockModalPayload["goalDraft"]["trackerSpecs"] {
  if (!trackers?.length) {
    return [];
  }

  return trackers
    .filter((tracker) => tracker.status !== "DELETE")
    .map((tracker) => {
      const title = tracker.title?.trim();
      if (!title) {
        return null;
      }
      const description = tracker.description?.trim();
      return {
        title,
        ...(description ? { description } : {}),
      };
    })
    .filter(
      (
        spec,
      ): spec is DiagnosisUnlockModalPayload["goalDraft"]["trackerSpecs"][number] =>
        spec != null,
    );
}

/** Goal 是否已激活（fetch 路径 Step3 默认判定，Plan 调用方应显式传 chrome.step3） */
export function isEstablishedGoal(goal: Goal): boolean {
  return goal.status === GoalStatusEnum.ACTIVE;
}

/**
 * 将 Goal 详情映射为弹窗 payload；解析/字段不足时返回 null。
 */
export function mapGoalToDiagnosisUnlockPayload(
  goal: Goal,
): DiagnosisUnlockModalPayload | null {
  const parsedContent = parseDiagnosisContentFields(goal.diagnosis_content);
  if (!parsedContent) {
    return null;
  }

  const title = goal.title?.trim();
  if (!title) {
    return null;
  }

  const fromTrackers = mapTrackersToSpecs(goal.trackers);
  const trackerSpecs =
    fromTrackers.length > 0
      ? fromTrackers
      : (parsedContent.embeddedTrackerSpecs ?? []);

  const summary =
    parsedContent.planSummary?.trim() || goal.review?.trim() || title;

  return {
    riskSummary: parsedContent.riskSummary,
    riskItems: parsedContent.riskItems,
    goalDraft: {
      goalId: goal.id,
      title,
      summary,
      trackerSpecs,
    },
  };
}
